import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Hook pour récupérer et gérer les modules activés du tenant
 * @param {boolean} isTenantSuspended - Indique si le tenant est suspendu
 * @param {boolean} checkingTenantStatus - Indique si la vérification du statut est en cours
 */
export function useModules(isTenantSuspended = false, checkingTenantStatus = false) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Attendre que la vérification du tenant soit terminée
    if (checkingTenantStatus) {
      return;
    }

    // Ne pas faire d'appels si le tenant est suspendu
    if (isTenantSuspended) {
      setLoading(false);
      return;
    }

    fetchModules();
    
    // Rafraîchir les modules toutes les 30 secondes pour détecter les changements
    const intervalId = setInterval(() => {
      if (!isTenantSuspended && !checkingTenantStatus) {
        fetchModules();
      }
    }, 30000); // 30 secondes
    
    // Écouter l'événement personnalisé pour rafraîchir immédiatement
    const handleModulesChanged = () => {
      if (!isTenantSuspended && !checkingTenantStatus) {
        fetchModules();
      }
    };
    window.addEventListener('modulesChanged', handleModulesChanged);
    
    // Nettoyer l'intervalle et l'événement lors du démontage
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('modulesChanged', handleModulesChanged);
    };
  }, [isTenantSuspended, checkingTenantStatus]);

  const fetchModules = async () => {
    // Ne pas faire d'appels si le tenant est suspendu ou en cours de vérification
    if (isTenantSuspended || checkingTenantStatus) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/tenant/modules`);
      setModules(response.data.enabled_modules || []);
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching modules:', err);
      setError(err);
      // En cas d'erreur, conserver les modules déjà chargés ou utiliser les modules de base
      if (modules.length === 0) {
        // Seulement au premier chargement, activer les modules de base
        setModules(['users', 'classes', 'subjects']);
      }
      setLoading(false);
    }
  };

  /**
   * Vérifie si un module est activé
   */
  const isModuleEnabled = (moduleName) => {
    return modules.includes(moduleName);
  };

  /**
   * Vérifie si au moins un des modules est activé
   */
  const isAnyModuleEnabled = (moduleNames) => {
    return moduleNames.some(name => modules.includes(name));
  };

  return {
    modules,
    loading,
    error,
    isModuleEnabled,
    isAnyModuleEnabled,
    refreshModules: fetchModules
  };
}
